import { BarChart3, CalendarRange, DatabaseZap, Download, Eye, MousePointerClick, TrendingUp, Users } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton, SkeletonCard } from "@/components/skeleton";
import { useAnalyticsRealtime } from "@/hooks/use-analytics-realtime";
import { useApi } from "@/hooks/use-api";
import { formatCurrency, formatMonthDay, formatNumber } from "@/lib/i18n";

interface AnalyticsDashboardData {
  from: string;
  to: string;
  generatedAt: string;
  kpis: {
    pageViews: number;
    uniqueVisitors: number;
    conversions: number;
    revenueCents: number;
  };
  trafficSources: Array<{
    source: string;
    medium: string;
    sessions: number;
  }>;
  funnel: Array<{
    eventType: string;
    label: string;
    count: number;
  }>;
  revenueTrend: Array<{
    date: string;
    revenueCents: number;
  }>;
  topPages: Array<{
    path: string;
    title: string | null;
    pageViews: number;
    uniqueVisitors: number;
  }>;
}

interface AnalyticsAnomalyData {
  type: string;
  severity: "warning" | "critical";
  message: string;
  recommendation: string;
}

function formatDateInput(value: Date): string {
  return value.toISOString().slice(0, 10);
}

function getDefaultTo(): string {
  return formatDateInput(new Date());
}

function getDefaultFrom(): string {
  const current = new Date();
  current.setUTCDate(current.getUTCDate() - 29);
  return formatDateInput(current);
}

function formatDashboardCurrency(cents: number): string {
  return formatCurrency(cents, "EUR", {
    maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
  });
}

function formatMediumLabel(medium: string): string {
  return medium
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function compactDate(value: string): string {
  return formatMonthDay(new Date(`${value}T00:00:00Z`));
}

const API_URL = import.meta.env.VITE_API_URL ?? "";

function KpiCard({
  icon,
  label,
  value,
  detail,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  detail: string;
}) {
  return (
    <article className="density-card stagger-item rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-5 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm text-[var(--color-muted-foreground)]">{label}</p>
          <p className="mt-2 text-2xl font-bold text-[var(--color-foreground)] tabular-nums">{value}</p>
        </div>
        <div className="rounded-full bg-[var(--color-muted)] p-3 text-[var(--color-primary)]">{icon}</div>
      </div>
      <p className="mt-3 text-xs text-[var(--color-muted-foreground)]">{detail}</p>
    </article>
  );
}

export default function AnalyticsDashboardPage() {
  const [dateRange, setDateRange] = useState(() => ({
    from: getDefaultFrom(),
    to: getDefaultTo(),
  }));
  const [exportFormat, setExportFormat] = useState<"csv" | "pdf">("csv");
  const [isExporting, setIsExporting] = useState(false);
  const { snapshot: realtimeSnapshot, isConnected: isRealtimeConnected } = useAnalyticsRealtime();

  const query = useMemo(
    () => new URLSearchParams({ from: dateRange.from, to: dateRange.to }).toString(),
    [dateRange.from, dateRange.to],
  );

  const { data, isLoading, error } = useApi<AnalyticsDashboardData>(`/v1/analytics/dashboard?${query}`);
  const { data: anomalyData } = useApi<AnalyticsAnomalyData[]>("/v1/analytics/anomalies");
  const anomalies = anomalyData ?? [];

  const handleExport = useCallback(async () => {
    setIsExporting(true);
    try {
      const exportQuery = new URLSearchParams({
        report: "dashboard",
        format: exportFormat,
        from: dateRange.from,
        to: dateRange.to,
      }).toString();
      const response = await fetch(`${API_URL}/v1/analytics/export?${exportQuery}`, {
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`Export failed (${response.status})`);
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = `analytics-dashboard-${dateRange.from}-${dateRange.to}.${exportFormat}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } finally {
      setIsExporting(false);
    }
  }, [dateRange.from, dateRange.to, exportFormat]);

  if (isLoading) {
    return (
      <div className="mx-auto max-w-7xl space-y-6 px-4 py-6">
        <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6 shadow-sm">
          <div className="flex items-center gap-2 text-sm font-medium text-[var(--color-foreground)]">
            <DatabaseZap className="h-4 w-4 animate-pulse" />
            Loading dashboard evidence
          </div>
          <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
            Checking mounted ClickHouse aggregates plus live session traffic for this tenant.
          </p>
        </section>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {[0, 1, 2, 3].map((index) => (
            <SkeletonCard key={index} className="h-28 rounded-2xl border-[var(--color-border)] bg-[var(--color-card)]" />
          ))}
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mx-auto max-w-7xl space-y-6 px-4 py-6">
        <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-2 shadow-sm">
          <EmptyState
            icon={<BarChart3 className="h-8 w-8" />}
            title="Analytics dashboard unavailable"
            description={error ?? "The mounted dashboard contract did not load."}
            variant="default"
          />
        </section>
      </div>
    );
  }

  const maxTrafficSessions = Math.max(...data.trafficSources.map((row) => row.sessions), 1);
  const maxFunnelCount = Math.max(...data.funnel.map((row) => row.count), 1);
  const maxRevenue = Math.max(...data.revenueTrend.map((row) => row.revenueCents), 1);

  return (
    <div className="density-gap mx-auto max-w-7xl space-y-6 px-4 py-6">
      <section className="density-card rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-6 shadow-sm">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 rounded-full bg-[var(--color-muted)] px-3 py-1 text-xs font-medium text-[var(--color-muted-foreground)]">
              <DatabaseZap className="h-3.5 w-3.5" />
              Mounted tenant analytics
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[var(--color-foreground)]">Analytics Dashboard</h1>
              <p className="mt-1 max-w-3xl text-sm text-[var(--color-muted-foreground)]">
                Real tenant dashboard evidence from the canonical analytics seam: ClickHouse-backed conversion and revenue aggregates, plus live traffic-source and top-page truth from the mounted page-session pipeline.
              </p>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm">
              <span className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
                <CalendarRange className="h-3.5 w-3.5" />
                From
              </span>
              <input
                type="date"
                value={dateRange.from}
                onChange={(event) => setDateRange((current) => ({ ...current, from: event.target.value }))}
                className="mt-2 w-full bg-transparent font-medium text-[var(--color-foreground)] outline-none"
              />
            </label>
            <label className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm">
              <span className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
                <CalendarRange className="h-3.5 w-3.5" />
                To
              </span>
              <input
                type="date"
                value={dateRange.to}
                onChange={(event) => setDateRange((current) => ({ ...current, to: event.target.value }))}
                className="mt-2 w-full bg-transparent font-medium text-[var(--color-foreground)] outline-none"
              />
            </label>
          </div>
          <div className="flex items-center gap-3">
            <label className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm">
              <span className="text-xs font-medium uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
                Export
              </span>
              <select
                value={exportFormat}
                onChange={(event) => setExportFormat(event.target.value as "csv" | "pdf")}
                className="mt-2 w-full bg-transparent font-medium text-[var(--color-foreground)] outline-none"
              >
                <option value="csv">CSV</option>
                <option value="pdf">PDF</option>
              </select>
            </label>
            <button
              type="button"
              onClick={() => void handleExport()}
              disabled={isExporting}
              className="inline-flex items-center gap-2 rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-muted)] disabled:opacity-50"
            >
              <Download className="h-4 w-4" />
              {isExporting ? "Exporting..." : `Export ${exportFormat.toUpperCase()}`}
            </button>
          </div>
        </div>
      </section>

      {anomalies.length > 0 && (
        <section className="density-card rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-6 shadow-sm" data-reveal>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Anomaly Alerts</h2>
              <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                Active tenant anomalies derived from mounted ad, traffic, revenue, reputation, and churn evidence.
              </p>
            </div>
            <div className="rounded-full bg-[var(--color-muted)] px-3 py-1 text-xs font-medium text-[var(--color-muted-foreground)]">
              {anomalies.length} active
            </div>
          </div>

          <div className="mt-5 grid gap-3 lg:grid-cols-2">
            {anomalies.map((anomaly) => (
              <article
                key={anomaly.type}
                className={`rounded-xl border p-4 ${
                  anomaly.severity === "critical"
                    ? "border-[var(--color-destructive)]/25 bg-[var(--color-destructive)]/5"
                    : "border-[var(--color-warning)]/25 bg-[var(--color-warning)]/5"
                }`}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] ${
                      anomaly.severity === "critical"
                        ? "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]"
                        : "bg-[var(--color-warning)]/10 text-[var(--color-warning)]"
                    }`}
                  >
                    {anomaly.severity}
                  </span>
                  <h3 className="text-sm font-semibold text-[var(--color-foreground)]">
                    {anomaly.type.replaceAll("_", " ")}
                  </h3>
                </div>
                <p className="mt-2 text-sm text-[var(--color-foreground)]">{anomaly.message}</p>
                <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">{anomaly.recommendation}</p>
              </article>
            ))}
          </div>
        </section>
      )}

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-reveal>
        <KpiCard icon={<Eye className="h-5 w-5" />} label="Page Views" value={formatNumber(data.kpis.pageViews)} detail="Live page.viewed touchpoints in this window." />
        <KpiCard icon={<Users className="h-5 w-5" />} label="Unique Visitors" value={formatNumber(data.kpis.uniqueVisitors)} detail="Distinct visitor IDs from mounted session and touchpoint truth." />
        <KpiCard icon={<MousePointerClick className="h-5 w-5" />} label="Conversions" value={formatNumber(data.kpis.conversions)} detail="ClickHouse event aggregates across won, confirmed, and completed conversion events." />
        <KpiCard icon={<TrendingUp className="h-5 w-5" />} label="Revenue" value={formatDashboardCurrency(data.kpis.revenueCents)} detail="Attributed revenue trend from ClickHouse revenue_by_period materialization." />
      </section>

      <section className="grid gap-6 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]" data-reveal>
        <article className="density-card rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-6 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-[var(--color-primary)]" />
              <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Live Right Now</h2>
            </div>
            <div className={`rounded-full px-3 py-1 text-xs font-medium ${isRealtimeConnected ? "bg-emerald-500/10 text-emerald-700" : "bg-[var(--color-muted)] text-[var(--color-muted-foreground)]"}`}>
              {isRealtimeConnected ? "Realtime connected" : "Polling every 10s"}
            </div>
          </div>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            Recent `page.viewed` evidence from the last {realtimeSnapshot?.windowSeconds ? Math.round(realtimeSnapshot.windowSeconds / 60) : 5} minutes.
          </p>

          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
              <p className="text-xs uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">Active visitors</p>
              <p className="mt-2 text-3xl font-bold text-[var(--color-foreground)]">
                {formatNumber(realtimeSnapshot?.activeVisitors ?? 0)}
              </p>
            </div>
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
              <p className="text-xs uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">Current page views</p>
              <p className="mt-2 text-3xl font-bold text-[var(--color-foreground)]">
                {formatNumber(realtimeSnapshot?.currentPageViews ?? 0)}
              </p>
            </div>
          </div>
        </article>

        <article className="density-card rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-6 shadow-sm">
          <div className="flex items-center gap-2">
            <Eye className="h-4 w-4 text-[var(--color-primary)]" />
            <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Live Top Pages</h2>
          </div>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            Top pages in the current five-minute activity window.
          </p>

          {!realtimeSnapshot || realtimeSnapshot.topPages.length === 0 ? (
            <EmptyState
              className="py-10"
              icon={<Eye className="h-8 w-8" />}
              title="No live page activity"
              description="Live top pages will appear once recent page.viewed touchpoints land for this tenant."
              variant="default"
            />
          ) : (
            <div className="mt-5 space-y-3">
              {realtimeSnapshot.topPages.map((page) => (
                <div key={page.path} className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-medium text-[var(--color-foreground)]">{page.title ?? page.path}</p>
                      <p className="mt-1 break-all text-xs text-[var(--color-muted-foreground)]">{page.path}</p>
                    </div>
                    <p className="text-sm font-semibold text-[var(--color-foreground)]">{formatNumber(page.pageViews)} live views</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </article>
      </section>

      <section className="grid gap-6 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]" data-reveal>
        <article className="density-card rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-6 shadow-sm">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-[var(--color-primary)]" />
            <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Traffic Sources</h2>
          </div>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            Session-source evidence from the mounted visitor session pipeline.
          </p>

          {data.trafficSources.length === 0 ? (
            <EmptyState
              className="py-10"
              icon={<Users className="h-8 w-8" />}
              title="No traffic sources in this window"
              description="Traffic-source rows will appear once live page sessions are recorded for the selected date range."
              variant="default"
            />
          ) : (
            <div className="mt-5 space-y-3">
              {data.trafficSources.map((row) => (
                <div key={`${row.source}-${row.medium}`} className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-medium text-[var(--color-foreground)]">{row.source}</p>
                      <p className="text-xs uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">{formatMediumLabel(row.medium)}</p>
                    </div>
                    <p className="text-sm font-semibold text-[var(--color-foreground)]">{formatNumber(row.sessions)} sessions</p>
                  </div>
                  <div className="mt-3 h-2 overflow-hidden rounded-full bg-[var(--color-muted)]">
                    <div
                      className="h-full rounded-full bg-[var(--color-primary)]"
                      style={{ width: `${Math.max((row.sessions / maxTrafficSessions) * 100, 8)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </article>

        <article className="density-card rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-6 shadow-sm">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-[var(--color-primary)]" />
            <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Conversion Funnel</h2>
          </div>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            Monthly deal-stage progression from the mounted ClickHouse funnel materialization.
          </p>

          <div className="mt-5 space-y-3">
            {data.funnel.map((step) => (
              <div key={step.eventType} className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
                <div className="flex items-center justify-between gap-4">
                  <p className="font-medium text-[var(--color-foreground)]">{step.label}</p>
                  <p className="text-sm font-semibold text-[var(--color-foreground)]">{formatNumber(step.count)}</p>
                </div>
                <div className="mt-3 h-2 overflow-hidden rounded-full bg-[var(--color-muted)]">
                  <div
                    className="h-full rounded-full bg-[var(--color-primary)]"
                    style={{ width: `${Math.max((step.count / maxFunnelCount) * 100, step.count > 0 ? 8 : 0)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </article>
      </section>

      <section className="grid gap-6 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]" data-reveal>
        <article className="density-card rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-6 shadow-sm">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-[var(--color-primary)]" />
            <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Revenue Trend</h2>
          </div>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            Daily attributed revenue from `revenue_by_period_mv`.
          </p>

          {data.revenueTrend.length === 0 ? (
            <EmptyState
              className="py-10"
              icon={<BarChart3 className="h-8 w-8" />}
              title="No revenue in this window"
              description="Revenue points appear once attributed conversions exist for the selected date range."
              variant="default"
            />
          ) : (
            <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-6">
              {data.revenueTrend.map((point) => (
                <div key={point.date} className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
                  <p className="text-xs uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">{compactDate(point.date)}</p>
                  <div className="mt-3 flex h-24 items-end">
                    <div
                      className="w-full rounded-t-lg bg-[var(--color-primary)]/85"
                      style={{ height: `${Math.max((point.revenueCents / maxRevenue) * 100, point.revenueCents > 0 ? 12 : 0)}%` }}
                    />
                  </div>
                  <p className="mt-3 text-sm font-semibold text-[var(--color-foreground)]">{formatDashboardCurrency(point.revenueCents)}</p>
                </div>
              ))}
            </div>
          )}
        </article>

        <article className="density-card rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-6 shadow-sm">
          <div className="flex items-center gap-2">
            <Eye className="h-4 w-4 text-[var(--color-primary)]" />
            <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Top Pages</h2>
          </div>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            Live page-session truth ranked by page views and unique visitors.
          </p>

          {data.topPages.length === 0 ? (
            <EmptyState
              className="py-10"
              icon={<Eye className="h-8 w-8" />}
              title="No page traffic in this window"
              description="Top pages appear once live visitor sessions are recorded."
              variant="default"
            />
          ) : (
            <div className="mt-5 space-y-3">
              {data.topPages.map((page) => (
                <div key={page.path} className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
                  <p className="font-medium text-[var(--color-foreground)]">{page.title ?? page.path}</p>
                  <p className="mt-1 break-all text-xs text-[var(--color-muted-foreground)]">{page.path}</p>
                  <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-[var(--color-muted-foreground)]">Page views</p>
                      <p className="font-semibold text-[var(--color-foreground)]">{formatNumber(page.pageViews)}</p>
                    </div>
                    <div>
                      <p className="text-[var(--color-muted-foreground)]">Unique visitors</p>
                      <p className="font-semibold text-[var(--color-foreground)]">{formatNumber(page.uniqueVisitors)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </article>
      </section>
    </div>
  );
}
